import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Descriptions, Form, Select, Space, Switch, Tag } from 'antd';

import { setAppTitle } from 'src/store/app';
import {
  fetchUserProfile,
  isLoadingProfileSelector,
  isUpdatingProfileSelector,
  profileSelector,
  updateUserProfile
} from 'src/store/profile';

import './profile.scss';

export const Profile = () => {
  const { t } = useTranslation('profile');

  const dispatch = useDispatch();

  const profile = useSelector(profileSelector);
  const isLoadingProfile = useSelector(isLoadingProfileSelector);
  const isUpdatingProfile = useSelector(isUpdatingProfileSelector);

  const [isEditing, setIsEditing] = useState(false);
  const [localProfile, setLocalProfile] = useState(profile);

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(setAppTitle(t('menu:profile')));
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    setLocalProfile(profile);
    form.setFieldsValue({
      isBotEnabled: profile?.isBotEnabled ?? true,
      language: profile?.language || 'en'
    });
  }, [form, profile]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.setFieldsValue({
      isBotEnabled: localProfile?.isBotEnabled ?? true,
      language: localProfile?.language || 'en'
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const response = await dispatch(updateUserProfile({ payload: values })).unwrap();

      if (response?.data) {
        setLocalProfile(response.data);
      } else {
        setLocalProfile((prev) => ({
          ...prev,
          ...values
        }));
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Profile[updateUserProfile]', error);
    }
  };

  const formatDate = (value) => {
    if (!value) return t('labels.not_available');

    return new Date(value).toLocaleString();
  };

  const identityName = useMemo(() => {
    return localProfile?.displayName || localProfile?.login || t('labels.unknown');
  }, [localProfile, t]);

  const avatarFallback = useMemo(() => {
    if (!identityName) return '?';

    return identityName.trim()[0]?.toUpperCase() || '?';
  }, [identityName]);

  const liveTag = localProfile?.isLive ? (
    <Tag color="green">{t('status.live')}</Tag>
  ) : (
    <Tag color="default">{t('status.offline')}</Tag>
  );

  const botTag = localProfile?.isBotEnabled ? (
    <Tag color="green">{t('status.enabled')}</Tag>
  ) : (
    <Tag color="red">{t('status.disabled')}</Tag>
  );

  return (
    <div className="profile">
      <div className="profile__content">
        <div className="profile__header">
          <div className="profile__header-actions">
            {!isEditing ? (
              <Button type="primary" onClick={handleEdit} disabled={isLoadingProfile}>
                {t('actions.edit')}
              </Button>
            ) : (
              <Space>
                <Button onClick={handleCancel}>{t('actions.cancel')}</Button>
                <Button type="primary" onClick={handleSave} loading={isUpdatingProfile}>
                  {t('actions.save')}
                </Button>
              </Space>
            )}
          </div>
        </div>

        <div className="profile__grid">
          <div className="profile__panel profile__panel--summary">
            <div className="profile__identity">
              <Avatar size={96} src={localProfile?.profileImageUrl} className="profile__avatar">
                {avatarFallback}
              </Avatar>
              <div className="profile__identity-text">
                <div className="profile__name">{identityName}</div>
                <div className="profile__login">@{localProfile?.login || '-'}</div>
              </div>
            </div>
            <div className="profile__tags">
              <Tag color="blue">{localProfile?.subscription || t('labels.not_available')}</Tag>
              {botTag}
              {liveTag}
            </div>
          </div>

          <div className="profile__panel profile__panel--details">
            {!isEditing ? (
              <Descriptions size="small" column={1} bordered className="profile__descriptions">
                <Descriptions.Item label={t('fields.display_name')}>
                  {localProfile?.displayName || t('labels.not_available')}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.login')}>
                  {localProfile?.login || t('labels.not_available')}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.twitch_id')}>
                  {localProfile?.twitchId || t('labels.not_available')}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.subscription')}>
                  {localProfile?.subscription || t('labels.not_available')}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.bot_enabled')}>{botTag}</Descriptions.Item>
                <Descriptions.Item label={t('fields.live_status')}>{liveTag}</Descriptions.Item>
                <Descriptions.Item label={t('fields.language')}>
                  {t(`languages.${localProfile?.language || 'en'}`)}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.last_live_at')}>
                  {formatDate(localProfile?.lastLiveAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.last_offline_at')}>
                  {formatDate(localProfile?.lastOfflineAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.created_at')}>
                  {formatDate(localProfile?.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t('fields.updated_at')}>
                  {formatDate(localProfile?.updatedAt)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Form
                form={form}
                layout="vertical"
                className="profile__form"
                initialValues={{
                  displayName: localProfile?.displayName || '',
                  profileImageUrl: localProfile?.profileImageUrl || '',
                  isBotEnabled: localProfile?.isBotEnabled ?? true,
                  language: localProfile?.language || 'en'
                }}
              >
                <Form.Item name="language" label={t('fields.language')}>
                  <Select
                    options={[
                      { value: 'en', label: t('languages.en') },
                      { value: 'ru', label: t('languages.ru') }
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="isBotEnabled"
                  label={t('fields.bot_enabled')}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
